import axiosInstance from "../api/axiosInstance";
import { useEffect, useState, useRef, useMemo } from "react"
import { useSelector } from "react-redux";
import { Container } from "react-bootstrap";
import { ClipLoader } from "react-spinners";
import { 
    MdOutlineLocalPharmacy, 
    MdPhone, 
    MdLocationOn, 
    MdAccessTime, 
    MdInfoOutline 
} from "react-icons/md";

const Pharmacy = () => {
    const [map, setMap] = useState(null); // 카카오 맵 객체를 상태로 관리
    const [pharmacyList, setPharmacyList] = useState([]);
    const [pharmacyInfoState, setPharmacyInfoState] = useState(null);
    const [loading, setLoading] = useState(false);
    const markersRef = useRef([]);

    const privateData = useSelector((state) => state.privateData);
    const pharmacy = useSelector((state) => state.pharmacy);

    const fnObj = useMemo(() => ({
        init() {
            fnObj.getKakaoMapInit();
        },
        mapObj: {
            mapContainer: null,
            mapOption: {
                center: new window.kakao.maps.LatLng(37.56212, 126.94083), // 지도의 중심좌표
                level: 3 // 지도의 확대 레벨
            },
            map: null
        },
        getKakaoMapInit() { // 카카오 맵 초기화
            let mapContainer = document.getElementById("map");
            let mapOption = fnObj.mapObj.mapOption;

            if (!map && mapContainer) {  // 맵이 생성되지 않았을 경우에만 초기화
                const newMap = new window.kakao.maps.Map(mapContainer, mapOption);
                newMap.setDraggable(true); // 드래그 가능
                newMap.setZoomable(true); // 줌인/줌아웃 가능

                setMap(newMap);  // 상태 업데이트
                fnObj.mapObj.map = newMap;  // 기존 fnObj에도 저장

                fnObj.getKaKaoMapMove(newMap);

                // 초기 좌표 기준으로 약국 데이터 로드
                const center = newMap.getCenter();
                fnObj.fetchPharmacies(center.getLat(), center.getLng());
            }
        },
        async fetchPharmacies(lat, lng) {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`${pharmacy.pharmacyWgsUrl}?serviceKey=${privateData.serviceKey}&WGS84_LAT=${lat}&WGS84_LON=${lng}`, {
                    headers: {
                        'Accept': 'application/xml',
                    }
                });
                let items = fnObj.getParseXMLToJSON(response.data).response.body.items.item;
                if (!items) {
                    setPharmacyList([]);
                    setPharmacyInfoState(null);
                    setLoading(false);
                    return;
                }

                let dataList = Array.isArray(items) ? items : [items];
                let returnList = [];
                for (let data of dataList) {
                    let wgsList = [parseFloat(data.latitude['#text']), parseFloat(data.longitude['#text'])];
                    let pharmacyObj = {
                        hpId: data.hpid['#text'],
                        wgsList: wgsList,
                        dutyName: data.dutyName['#text'],
                        dutyTel1: data.dutyTel1['#text'],
                        dutyAddr: data.dutyAddr['#text'],
                        startTime: data.startTime['#text'],
                        endTime: data.endTime['#text']
                    }
                    returnList.push(pharmacyObj);
                }
                setPharmacyList(returnList);
                if (returnList.length > 0) {
                    setPharmacyInfoState(returnList[0]);
                } else {
                    setPharmacyInfoState(null);
                }
            } catch (error) {
                console.log(error);
                alert('해당 지역은 조회되지 않습니다.');
            }
            setLoading(false);
        },
        getKaKaoMapMove(currentMap) { // 카카오 맵 이동/줌 이벤트 핸들러
            window.kakao.maps.event.addListener(currentMap, 'dragend', () => {
                let latlng = currentMap.getCenter();
                if (latlng.getLat() && latlng.getLng()) {
                    fnObj.fetchPharmacies(latlng.getLat(), latlng.getLng());
                }
            });
        },
        getParseXMLToJSON(xmlString) {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlString, "text/xml");

            const xmlToJson = (node) => {
                const obj = {};
                if (node.nodeType === 1) { // Element node
                    if (node.attributes.length > 0) {
                        obj["@attributes"] = {};
                        for (let attr of node.attributes) {
                            obj["@attributes"][attr.nodeName] = attr.nodeValue;
                        }
                    }
                } else if (node.nodeType === 3) { // Text node
                    return node.nodeValue.trim();
                }

                if (node.childNodes.length > 0) {
                    for (let child of node.childNodes) {
                        const nodeName = child.nodeName;
                        const jsonChild = xmlToJson(child);

                        if (jsonChild) {
                            if (obj[nodeName] === undefined) {
                                obj[nodeName] = jsonChild;
                            } else {
                                if (!Array.isArray(obj[nodeName])) {
                                    obj[nodeName] = [obj[nodeName]];
                                }
                                obj[nodeName].push(jsonChild);
                            }
                        }
                    }
                }
                return obj;
            };

            return { [xmlDoc.documentElement.nodeName]: xmlToJson(xmlDoc.documentElement) };
        }
    }), [pharmacy.pharmacyWgsUrl, privateData.serviceKey, map]);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=252789b38c66bb62dc7212f1c1176ea0&libraries=services,clusterer,drawing`;
        script.async = true;
        document.head.appendChild(script);

        script.onload = () => {
            fnObj.getKakaoMapInit();
        };

        return () => {
            document.head.removeChild(script);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fnObj]);

    useEffect(() => {
        if (map && pharmacyList.length > 0) {
            let pointList = [];
            for (let wgs of pharmacyList) {
                pointList.push(new window.kakao.maps.LatLng(parseFloat(wgs.wgsList[0]), parseFloat(wgs.wgsList[1])));
            }

            // 기존 마커 제거
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];

            let i, marker;
            const newMarkers = [];
            for (i = 0; i < pointList.length; i++) {
                const item = pharmacyList[i];
                marker = new window.kakao.maps.Marker({ map: map, position: pointList[i], clickable: true });
                marker.setMap(map);
                newMarkers.push(marker);

                let iwContent = `
                    <div style="padding: 10px; font-family: sans-serif; font-size: 13px; line-height: 1.4; min-width: 150px; max-width: 220px; border-radius: 8px;">
                        <div style="font-weight: 700; color: #1a202c; margin-bottom: 5px; font-size: 13px; word-break: keep-all;">
                            ${item.dutyName}
                        </div>
                        <div style="font-size: 12px; color: #3182ce; font-weight: 600;">
                            ☎ <a href="tel:${item.dutyTel1}" style="color: #3182ce; text-decoration: none;" target="_blank">${item.dutyTel1}</a>
                        </div>
                    </div>
                `;

                let infowindow = new window.kakao.maps.InfoWindow({
                    position: pointList[i],
                    content: iwContent,
                    disableAutoPan: true
                });

                infowindow.open(map, marker);

                // 마커 클릭 시 해당 약국 상태 선택
                window.kakao.maps.event.addListener(marker, 'click', () => {
                    setPharmacyInfoState(item);
                });
            }
            markersRef.current = newMarkers;
        }
    }, [pharmacyList, map]);

    const handleSelectPharmacy = (item) => {
        setPharmacyInfoState(item);
        if (map) {
            const moveLatLng = new window.kakao.maps.LatLng(item.wgsList[0], item.wgsList[1]);
            map.panTo(moveLatLng);
        }
    };

    const formatTime = (timeStr) => {
        if (!timeStr || timeStr.length < 4) return "-";
        return `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;
    };

    return (
        <div className="medical-wrapper">
            {
                loading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner">
                            <ClipLoader color="#ffffff" size={50} />
                            <p className="mt-2">로딩 중...</p>
                        </div>
                    </div>
                )
            }
            <Container>
                {/* 상단 헤더 */}
                <div className="medical-filter-card d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h4 className="m-0 fw-bold d-flex align-items-center gap-2">
                        <MdOutlineLocalPharmacy className="text-primary" /> 주변 약국 검색
                    </h4>
                    <span className="text-muted small">
                        지도를 마우스로 드래그하면 중심 좌표 기준으로 약국이 실시간 조회됩니다.
                    </span>
                </div>

                {/* 2컬럼 레이아웃 */}
                <div className="medical-grid-layout">
                    {/* 카카오 맵 */}
                    <div id="map" className="medical-map-container"></div>

                    {/* 약국 목록 / 상세 정보 */}
                    <div className="medical-panel">
                        <div className="medical-panel-header">
                            <h5 className="medical-panel-title">
                                <MdOutlineLocalPharmacy className="text-primary" />
                                내 주변 약국 목록 ({pharmacyList.length}곳)
                            </h5>
                        </div>

                        {/* 상세 요약 카드 (선택된 약국 정보) */}
                        {pharmacyInfoState && (
                            <div className="p-3 border-bottom bg-light">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h6 className="fw-bold m-0 text-primary" style={{ fontSize: '1.05rem' }}>
                                        {pharmacyInfoState.dutyName}
                                    </h6>
                                    <a href={`tel:${pharmacyInfoState.dutyTel1}`} className="btn btn-sm btn-outline-primary rounded-pill py-1 px-2 d-flex align-items-center gap-1 font-weight-bold">
                                        <MdPhone size={14} /> 전화 걸기
                                    </a>
                                </div>
                                <div className="d-flex flex-column gap-1 small text-secondary">
                                    <div className="d-flex align-items-center gap-1">
                                        <MdLocationOn className="text-secondary" /> {pharmacyInfoState.dutyAddr}
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <MdAccessTime className="text-secondary" /> 
                                        영업시간: {formatTime(pharmacyInfoState.startTime)} ~ {formatTime(pharmacyInfoState.endTime)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 약국 리스트 */}
                        <div className="medical-panel-content medical-scrollbar">
                            {pharmacyList.length > 0 ? (
                                pharmacyList.map((item) => (
                                    <div 
                                        key={item.hpId}
                                        className={`pharmacy-item-card ${pharmacyInfoState?.hpId === item.hpId ? 'active' : ''}`}
                                        onClick={() => handleSelectPharmacy(item)}
                                    >
                                        <div className="fw-bold d-flex align-items-center gap-1 mb-1" style={{ fontSize: '0.95rem' }}>
                                            <MdOutlineLocalPharmacy className="text-danger" />
                                            {item.dutyName}
                                        </div>
                                        <div className="text-muted text-truncate mb-1" style={{ fontSize: '0.8rem' }}>
                                            {item.dutyAddr}
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center text-secondary" style={{ fontSize: '0.78rem' }}>
                                            <span>☎ {item.dutyTel1}</span>
                                            <span className="fw-semibold">
                                                {formatTime(item.startTime)} ~ {formatTime(item.endTime)}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted text-center p-4">
                                    <MdInfoOutline size={40} className="mb-2 text-secondary" style={{ opacity: 0.5 }} />
                                    <p className="small">현재 지도 중심에 탐색된 약국이 없습니다.</p>
                                    <p className="small text-secondary">지도를 조금 드래그해 다른 곳을 탐색해보세요.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    )
}

export { Pharmacy };